import React, { Fragment, useEffect, useRef, useState } from 'react';
import Config from '../../config';
import {
    Button,
    Card,
    Grid as GridBase,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CardContent,
    Toolbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Moment from 'react-moment';
import Helper, { formatAddress, triggerSubmit } from '../../helpers';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import TextFieldBase from '../../components/text_field';
import DocumentLink from '../../components/document_link';
import ContentLoader from '../../components/content_loader';
import axios from 'axios';
import { InvoiceCalculator } from '../documents/invoice_details';
import { ItemAttributes, HeaderAttributes } from '../documents/document_components';
import type { InvoiceDocument } from '../documents/types';

// Relax MUI/untyped legacy components to `any` so their custom/v7 props
// type-check (behavior unchanged). Matches documents/invoice_details.tsx.
const Grid: any = GridBase;
const Form: any = FormBase;
const Field: any = FieldBase;
const TextField: any = TextFieldBase;

const StyledTable = styled(Table)({
    flex: '1',
    '& td': {
        padding: '5px'
    },
    '& th': {
        padding: '5px'
    }
});

interface OrderFormProps {
    order?: string;
    shipment?: string;
    setForm: (ref: any) => void;
    onSubmit: (id: string) => void;
}

function initOrder(order: any, includeCancellation?: boolean){
    order.InvoiceDate = new Date().toISOString().split("T")[0];
    order.InvoiceNumber = '';
    order.Invoices = [];
    order.InvoiceLines = order.OrderLines;
    order.subtotal = (order.InvoiceLines || []).reduce((sum, value) => sum + Number((value.Price || 0) * (value.Quantity || 0)), 0);
    order.InvoiceTotal = order.TotalAmount;
    order.taxTotal = (order.Taxes || []).reduce((sum, value) => sum + Number(value.TaxAmount || 0), 0);
    order.discountTotal = (order.Discounts || []).reduce((sum, value) => sum + Number(value.DiscountAmount || 0), 0);
    if(!order.InvoiceTotal){
        order.InvoiceTotal = order.subtotal + order.taxTotal - order.discountTotal + Number(order.HandlingAmount || 0) + Number(order.DropshipAmount || 0);
    }
    if(includeCancellation){
        for(var i = 0; i < order.InvoiceLines.length; i++){
            var line = order.InvoiceLines[i];
            var qtyCancelled = parseInt(((line.ExtendedAttributes || []).find(r => r.Name === "QtyCancelled") || {}).Value);
            if(!isNaN(qtyCancelled)){
                line.Quantity -= qtyCancelled;
                if(line.Quantity < 0){
                    line.Quantity = 0;
                }
            }
        }
    }
    delete order.OrderLines;
    for(var z = 0; z < order.InvoiceLines.length; z++){
        var item = order.InvoiceLines[z];
        if(item.LineNumber === null || typeof item.LineNumber === 'undefined'){
            item.LineNumber = z + 1;
        }
        item.Price = item.Price || 0;
        item.ExtendedPrice = item.Price * item.Quantity;
        item.OrderQuantity = item.Quantity;
        if(!item.ExtendedAttributes){
            item.ExtendedAttributes = [];
        }
    }
}

function initShipment(ship: any){
    ship.OrderLines = ship.ShipmentLines;
    delete ship.ShipmentLines;
    initOrder(ship);
}

const OrderForm = (props: OrderFormProps) => {
    const { order, shipment, setForm, onSubmit } = props;
    const [errors, setErrors] = useState<string[]>([]);
    const [showMessage, setShowMessage] = useState<boolean>(false);
    const [doc, setDoc] = useState<InvoiceDocument | undefined>(undefined);

    useEffect(() => {
        (async () => {
            if(order){
                var loadedDoc = (await axios.get(Config.api + `/api/v1/orders/${order}`)).data.Body.SalesOrder;
                initOrder(loadedDoc, true);
                setDoc(loadedDoc);
            }else if(shipment){
                var shipDoc = (await axios.get(Config.api + `/api/v1/shipments/${shipment}`)).data.Body.Shipment;
                initShipment(shipDoc);
                setDoc(shipDoc);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (doc: any) => {
        var invoice = Object.assign({}, doc);
        invoice.InvoiceLines = [];
        for(var z = 0; z < doc.InvoiceLines.length; z++){
            var item = doc.InvoiceLines[z];
            if(item.Quantity > 0){
                var invItem = Object.assign(item, {});
                invoice.InvoiceLines.push(invItem);
            }
        }
        let errors = [];

        if(invoice.InvoiceLines.length > 0){
            try{
                var resp = (await axios.post(Config.api + '/api/v1/invoices', invoice, {headers: {SourceSystem: "Portal"}})).data;
                doc.Invoices.push(resp.Body.LogicbrokerKey);
            }catch(e: any){
                if(e.response && e.response.data){
                    errors.push("Error creating invoice: " + new Helper().getApiErrors(e.response.data).join("\n"));
                }
            }
        } else {
            setErrors(["You must invoice at least one item."]);
            setShowMessage(true);
            return;
        }

        await onSubmit(doc.Identifier.LogicbrokerKey);
        setDoc(doc);
        setErrors(errors);
        setShowMessage(true);
    }

    if(!doc){
        return <ContentLoader preserveAspectRatio='none' style={{height: '8em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>;
    }

    var toolbar = <Toolbar className='lbtoolbar'><span style={{marginRight: '0.25rem'}}>Order {doc.PartnerPO} placed on</span><Moment format='MM/DD/YYYY'>{doc.OrderDate || doc.DocumentDate}</Moment></Toolbar>;
    if(shipment){
        toolbar = <Toolbar className='lbtoolbar'><span style={{marginRight: '0.25rem'}}>Shipment {doc.ShipmentNumber} for order {doc.PartnerPO} sent on</span><Moment format='MM/DD/YYYY'>{doc.ShipmentDate || doc.DocumentDate}</Moment></Toolbar>;
    }

    if(showMessage){
        return (<Card>
            {toolbar}
            <CardContent>
                <Grid container spacing={2}>
                    {doc.Invoices.map((s: any) => <div key={s}>Created invoice <DocumentLink type='Invoice' id={s}>#{s}</DocumentLink></div>)}
                    {errors.map((e, index) => <div key={index}>{e}</div>)}
                    {errors.length > 0 &&
                        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                            <Button variant='outlined' onClick={() => { setShowMessage(false); setErrors([]); }}>Retry</Button>
                        </Grid>
                    }
                </Grid>
            </CardContent>
        </Card>);
    }
        let currencyCode = (doc || {}).Currency;
        return <Form
    onSubmit={handleSubmit}
    initialValues={doc}
    decorators={[InvoiceCalculator]}
    mutators={{
      ...arrayMutators
    }}
    render={({ handleSubmit, pristine, invalid, values }) => (
      <form onSubmit={handleSubmit} ref={form => setForm(form)}>
      <Card>
          {toolbar}
          <CardContent>
          <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 6 }} style={{textAlign: 'left'}}>Ship To: {formatAddress(doc.ShipToAddress)}</Grid>
          <Grid size={{ xs: 12, sm: 12, md: 6 }} style={{textAlign: 'left'}}>Bill To: {formatAddress(doc.BillToAddress)}</Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12 }} style={{display: 'flex', flexWrap: 'wrap', gap: '1em'}}>
              <Field style={{width: '12rem'}} label='Invoice Number' name={`InvoiceNumber`} type='text' component={TextField} />
              <Field label='Invoice Date' name={`InvoiceDate`} type='date' component={TextField} />
              <Field currency currencyCode={currencyCode} style={{width: '7rem'}} component={TextField} label='Discount' name='discountTotal'/>
              <Field currency currencyCode={currencyCode} style={{width: '7rem'}} component={TextField} label='Shipping/Freight' name='HandlingAmount'/>
              <Field currency currencyCode={currencyCode} style={{width: '7rem'}} component={TextField} label='Dropship Fee' name='DropshipAmount'/>
              <Field currency currencyCode={currencyCode} style={{width: '7rem'}} component={TextField} label='Tax' name='taxTotal'/>
              <Field currency currencyCode={currencyCode} style={{width: '7rem'}} label='Invoice Total' name={`InvoiceTotal`} component={TextField} />
              <HeaderAttributes itemStyle={{width: '10em'}}/>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12 }} style={{display: 'flex'}}>
          <StyledTable>
          <TableHead>
              <TableRow>
                  <TableCell style={{width: '5em'}}>Line</TableCell>
                  <TableCell style={{width: '12em'}}>SKU</TableCell>
                  <TableCell style={{width: '12em'}}>Partner SKU</TableCell>
                  <TableCell style={{width: '12em'}}>UPC</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell style={{width: '7em'}}>Quantity</TableCell>
                  <TableCell style={{width: '7em'}}>Invoice Quantity</TableCell>
                  <TableCell style={{width: '7em'}}>Price</TableCell>
                  <TableCell style={{width: '7em'}}>Extended Price</TableCell>
              </TableRow>
          </TableHead>
          <TableBody>
          <FieldArray name={`InvoiceLines`}>
          {({fields}) =>
              fields.map((name, index) => <Fragment key={name}><TableRow>
              <TableCell>{fields.value[index].LineNumber}</TableCell>
              <TableCell>{fields.value[index].ItemIdentifier.SupplierSKU}</TableCell>
              <TableCell>{fields.value[index].ItemIdentifier.PartnerSKU}</TableCell>
              <TableCell>{fields.value[index].ItemIdentifier.UPC}</TableCell>
              <TableCell>{fields.value[index].Description}</TableCell>
              <TableCell>{fields.value[index].OrderQuantity}</TableCell>
              <TableCell><Field style={{width: '100%'}} name={`${name}.Quantity`} inputProps={{min: 0, max: fields.value[index].OrderQuantity}} component={TextField} type='number'/></TableCell>
              <TableCell><Field currency currencyCode={currencyCode} style={{width: '100%'}} name={`${name}.Price`} component={TextField}/></TableCell>
              <TableCell><Field currency currencyCode={currencyCode} disabled style={{width: '100%'}} name={`${name}.ExtendedPrice`} component={TextField}/></TableCell>
              </TableRow>
              <ItemAttributes name={name} item={fields.value[index]}/>
          </Fragment>)
          }
          </FieldArray>
          </TableBody>
          </StyledTable>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12 }}><Button variant='contained' color='primary' type='submit' disabled={invalid || (values.InvoiceLines.every(o => (!o.Quantity || o.Quantity <= 0)))} onClick={handleSubmit}>Submit</Button></Grid>
          </Grid>
          </CardContent>
          </Card>
        </form>
    )}/>;
}

const InvoiceMultiple = (props: any) => {
    const [orderIds, setOrderIds] = useState<string[]>([]);
    const [shipmentIds, setShipmentIds] = useState<string[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [showError, setShowError] = useState<boolean>(false);
    const [submitted, setSubmitted] = useState<string[]>([]);
    const [allSubmitted, setAllSubmitted] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean | undefined>(undefined);
    const forms = useRef<Record<string, any>>({});

    useEffect(() => {
        var ids: any = new Helper().queryString()['orderids'];
        if(ids){
            ids = ids.split(',');
        }
        var shipmentIds: any = new Helper().queryString()['shipmentids'];
        if(shipmentIds){
            shipmentIds = shipmentIds.split(',');
        }
        setOrderIds(ids || []);
        setShipmentIds(shipmentIds || []);
        if((!ids || ids.length === 0) && (!shipmentIds || shipmentIds.length === 0)){
            //TODO: update state to show error
            return;
        }
    }, []);

    const submitAll = async () => {
        setSubmitting(true);
        try{
            let docsToSubmit = [...orderIds, ...shipmentIds].filter(r => submitted.filter(x => x === r).length === 0);
            for(const id of docsToSubmit){
                let form = forms.current[id];
                if(form){
                    await new Promise(resolve => {
                        setTimeout(resolve, 1000);
                      });
                    triggerSubmit(form);
                }
            }
        }catch{}
        setSubmitting(false);
    }

    const setForm = (id: string, ref: any) => {
        let order = orderIds.filter(r => r === id)[0];
        let shipment = shipmentIds.filter(r => r === id)[0];
        if((!order && !shipment) || !ref){
            return;
        }
        forms.current[id] = ref;
    }

    const onSubmit = (id: string) => {
        setSubmitted(prevSubmitted => {
            let submitted = [...prevSubmitted, id];
            let ordersNotSubmitted = orderIds.filter(r => submitted.filter(x => x === r).length === 0).length;
            let shipmentsNotSubmitted = shipmentIds.filter(r => submitted.filter(x => x === r).length === 0).length;
            let allSubmitted = ordersNotSubmitted === 0 && shipmentsNotSubmitted === 0;
            setAllSubmitted(allSubmitted);
            return submitted;
        });
    }

    if(orderIds.length > 0 || shipmentIds.length > 0){
        return <Grid container spacing={2}>
            {orderIds.map((o) => <Grid key={o} size={{ xs: 12, sm: 12, md: 12 }}><OrderForm setForm={(ref: any) => setForm(o, ref)} onSubmit={onSubmit} order={o}/></Grid>)}
            {shipmentIds.map((o) => <Grid key={o} size={{ xs: 12, sm: 12, md: 12 }}><OrderForm setForm={(ref: any) => setForm(o, ref)} onSubmit={onSubmit} shipment={o}/></Grid>)}
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <Card>
                    <CardContent>
                        <Button variant='contained' style={{marginLeft: '1em'}} color='primary' onClick={submitAll} disabled={submitting || allSubmitted}>Submit all</Button>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>;
    }
    return <div/>;
}

export default InvoiceMultiple;
